<!-- TenantSelectionPopup.vue -->
<template>
  <div v-if="show" class="popup-overlay" @click.self="$emit('close')">
    <div class="popup-content" @click.stop>
      <div class="popup-header">
        <h3>{{ $t('tenantSelectionPopup.title') }}</h3>
        <button class="close-button" @click="$emit('close')">&times;</button>
      </div>
      <div class="popup-body">
        <p>{{ $t('tenantSelectionPopup.description') }}</p>
        <div class="select-wrapper">
          <select v-model="selectedTenantId" class="tenant-select" :disabled="loading">
            <option value="">{{ loading ? $t('tenantSelectionPopup.loadingTenants') : $t('tenantSelectionPopup.selectTenant') }}</option>
            <option v-if="showMasterTenant" value="master">{{ $t('tenantSelectionPopup.masterTenant') }}</option>
            <option v-for="tenant in tenants" :key="tenant.id" :value="tenant.id" :disabled="!tenant.is_active">{{ tenant.tenant_name }} {{ !tenant.is_active ? $t('tenantSelectionPopup.inactive') : '' }}</option>
          </select>
        </div>
        <div v-if="error" class="error">{{ error }}</div>
      </div>
      <div class="popup-footer">
        <button class="cancel-btn" @click="$emit('close')">{{ $t('tenantSelectionPopup.cancel') }}</button>
        <button class="proceed-btn" :disabled="!selectedTenantId || loading" @click="handleProceed">
          {{ loading ? $t('tenantSelectionPopup.loading') : $t('tenantSelectionPopup.proceed') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, getCurrentInstance } from 'vue'
import { get } from '@/utils/api'
import { isAiMethodBedrockForTenant } from '@/utils/tenantUtils.js'

export default {
  name: 'TenantSelectionPopup',
  props: {
    show: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['close', 'select'],
  setup(props, { emit }) {
    // Get current instance to access $t function
    const instance = getCurrentInstance()
    const $t = instance.proxy.$t
    const tenants = ref([])
    const selectedTenantId = ref('')
    const loading = ref(false)
    const error = ref('')
    const showMasterTenant = ref(false)

    const checkMasterTenantAccess = () => {
      const userRole = localStorage.getItem('userRole')
      const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')

      // Check for master tenant read permission
      const readPermissionMaster = permissions.some((p) => p.object_name === 'MASTER_CONFIGURE_RD' && p.is_active)

      // Show Master tenant option if user is Super Admin or Admin AND has master read permission
      showMasterTenant.value = readPermissionMaster && (userRole === 'Super Admin' || userRole === 'Admin')

      console.log('🏠 TenantSelectionPopup: Master tenant access check:', {
        userRole,
        readPermissionMaster,
        showMasterTenant: showMasterTenant.value,
      })
    }

    const fetchTenants = async () => {
      loading.value = true
      error.value = ''
      try {
        const response = await get('/v1/tenants?page=1&pageSize=100000')
        if (response?.data?.code === 200) {
          // Store tenants from the response, filtering active ones and mapping for dropdown
          tenants.value = (response.data.data.tenants || []).map((tenant) => ({
            id: tenant.id,
            tenant_name: tenant.tenant_name,
            is_active: tenant.is_active,
          }))

          // Check if user should see Master tenant option
          checkMasterTenantAccess()
        } else {
          error.value = $t('tenantSelectionPopup.errors.failedToLoadTenants')
        }
      } catch (err) {
        error.value = $t('tenantSelectionPopup.errors.errorLoadingTenants')
        console.error('Error fetching tenants:', err)
      } finally {
        loading.value = false
      }
    }

    const handleProceed = async () => {
      if (!selectedTenantId.value || loading.value) return

      loading.value = true
      error.value = ''

      try {
        // Handle Master tenant selection
        if (selectedTenantId.value === 'master') {
          console.log('🏠 TenantSelectionPopup: Master tenant selected')
          emit('select', {
            id: 'master',
            name: $t('tenantSelectionPopup.masterTenant'),
            selectedTenantHasBedrock: false, // Master tenant doesn't use Bedrock
          })
          return
        }

        // Find selected tenant details
        const selectedTenant = tenants.value.find((t) => t.id === selectedTenantId.value)
        if (selectedTenant) {
          // Check if tenant uses Bedrock
          const usesBedrock = await isAiMethodBedrockForTenant(selectedTenantId.value)

          console.log('🏠 TenantSelectionPopup: Regular tenant selected:', {
            id: selectedTenant.id,
            name: selectedTenant.tenant_name,
            usesBedrock,
          })

          emit('select', {
            id: selectedTenant.id,
            name: selectedTenant.tenant_name,
            selectedTenantHasBedrock: usesBedrock,
          })
        }
      } catch (err) {
        error.value = $t('tenantSelectionPopup.errors.errorProcessingSelection')
        console.error('Error in handleProceed:', err)
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      fetchTenants()
    })

    watch(
      () => props.show,
      (newVal) => {
        if (newVal && tenants.value.length === 0) {
          fetchTenants()
        }
      }
    )

    return {
      tenants,
      selectedTenantId,
      loading,
      error,
      showMasterTenant,
      handleProceed,
    }
  },
}
</script>

<style scoped>
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.popup-content {
  background: white;
  border-radius: 8px;
  width: 400px;
  max-width: 90vw;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.popup-header {
  padding: 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.popup-header h3 {
  margin: 0;
  color: var(--primary-color);
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
}

.popup-body {
  padding: 20px;
}

.select-wrapper {
  margin-top: 15px;
}

.tenant-select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.tenant-select option:disabled {
  color: #999;
  font-style: italic;
  background-color: #f5f5f5;
}

.popup-footer {
  padding: 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.cancel-btn,
.proceed-btn {
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.cancel-btn {
  background: white;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
}

.proceed-btn {
  background: var(--primary-color);
  border: none;
  color: white;
}

.proceed-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.loading {
  text-align: center;
  color: #666;
  margin-top: 10px;
}

.error {
  color: #dc3545;
  margin-top: 10px;
  font-size: 14px;
}
</style>
